import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import bgImage from "../../components/UseCases/assets/UseCasesBgImage.svg";
import useSEO from "../../hooks/useSEO";

const TermsOfUse = () => {

  useSEO({
    title: "Terms of Use - Beanbag AI",
    description:
      "Read Beanbag AI's Terms of Use to understand the legal terms and conditions governing your use of our platform and services.",
    canonical: "https://www.beanbag.ai/termsofuse",
    keywords:
      "terms of use, terms and conditions, user agreement, legal terms, platform usage, Beanbag AI",

    // Open Graph tags
    ogTitle: "Terms of Use - Beanbag AI",
    ogDescription:
      "Legal terms and conditions governing the use of Beanbag AI's platform and services.",
    ogImage: "https://www.beanbag.ai/images/terms-social-card.jpg",
    ogUrl: "https://www.beanbag.ai/termsofuse",
    ogType: "article",

    // Twitter Card tags
    twitterCard: "summary_large_image",
    twitterSite: "@BeanbagAI",

    // Additional meta tags
    robots: "index, follow",
    customMeta: {
      "article:published_time": "2025-03-20",
      "article:modified_time": "2025-03-20",
    },

    // JSON-LD structured data
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://www.beanbag.ai/termsofuse#webpage",
      headline: "Terms of Use",
      description:
        "Read Beanbag AI's Terms of Use to understand the legal terms and conditions governing your use of our platform and services.",
      url: "https://www.beanbag.ai/termsofuse",
      datePublished: "2025-03-20",
      dateModified: "2025-03-20",
      publisher: {
        "@type": "Organization",
        name: "Beanbag AI",
        logo: {
          "@type": "ImageObject",
          url: "https://www.beanbag.ai/logo192.png",
        },
      },
      mainEntity: {
        "@type": "Article",
        headline: "Terms of Use",
        datePublished: "2025-03-20",
        dateModified: "2025-03-20",
        author: {
          "@type": "Organization",
          name: "Beanbag AI",
        },
        publisher: {
          "@type": "Organization",
          name: "Beanbag AI",
          logo: {
            "@type": "ImageObject",
            url: "https://www.beanbag.ai/logo192.png",
          },
        },
      },
    },
  });
  // Define sections to match the blog post format
  const sections = [
    {
      id: "introduction",
      title: "",
      content: `Welcome to Beanbag AI. When you visit, view, use, or access our website Beanbag AI.com and any applicable subdomains thereof, or any applications, mobile applications, functionalities, content, materials, or other online services provided by Beanbag AI i.e. Remote Community Inc. (collectively, the “Platform”), whether as a guest or a registered user, you are agreeing to the following terms and conditions (“Terms of Use”), so please take a few minutes to read over the Terms of Use below.`,
    },
    {
      id: "acceptance",
      title: "Acceptance of Terms of Use",
      content:
        "If you do not agree to these Terms of Use, you are not permitted to use the Platform. Beanbag AI can change these Terms of Use at any time without any notice to you. It is your responsibility to review these Terms of Use, as it creates a binding legal agreement between you and Beanbag AI. If you use the Platform after we have changed any of the Terms of Use, you are agreeing to all of the changes. To the extent that you or your employer (to the extent you are using the Platform in your capacity as an agent of your employer) has entered into another written agreement with Beanbag AI that contains terms that directly conflict with any terms of these Terms of Use, then the conflicted terms set forth in such other agreement will control.",
    },
    {
      id: "Eligibility",
      title: "Eligibility",
      content: `Any access or use of the Platform by anyone under the age of 18 is prohibited. By agreeing to these Terms of Use, you represent and warrant that you are 18 years of age or older and have the legal capacity to enter into and abide by these Terms of Use.`,
    },
    {
      id: "Privacy",
      title: "Privacy",
      content: `Any personal information submitted in connection with your use of the Platform is subject to our privacy policy found at https://www.beanbag.ai/privacy-policy/ (the “Privacy Policy”), which is hereby incorporated by this reference. We will use information about you that we obtain either directly from you or that we obtain through your use of the Platform in accordance with our Privacy Policy solely in furtherance of providing you and improving the Platform.
`,
    },
    {
      id: "PRIVACY-usage",
      title: "Account and Security",
      content: `You may need to set up an account in order to use some of the features of the Platform. When you are setting up your account, you must give us accurate and complete information. This means that you cannot set up an account using a name or contact information that does not apply to you, and you must provide accurate and current information on all registration forms that are part of the Platform. By accessing any such account, you represent and warrant that you have been authorized to do so by Beanbag AI and are doing so only using credentials that have been issued to you by Beanbag AI. You may not use any account belonging to any other person without Beanbag AI’s express, prior, written permission. You have complete responsibility for your account and everything that happens on your account. This means you need to be careful with your password. If you find out that someone is using your account without your permission, you must let us know immediately. You may not transfer your account to someone else and doing so could subject you to civil or criminal liability. We are not liable for any damages or losses caused by someone using your account without your permission.`,
    },
    {
      id: "material-usage",
      title: "Proprietary Materials",
      content: `The Platform contains copyrighted materials, trademarks, proprietary and confidential information, and intellectual property of Beanbag AI and licensors of Beanbag AI (collectively, “Proprietary Materials”), including without limitation source code, video, text, software, photos, graphics, images, music, and sound. You agree not to modify, publish, transmit, participate in the transfer or sale of, create derivative works of, or in any way exploit, in whole or in part, any Proprietary Materials. Proprietary Materials may only be accessed through the Platform, and not by or from any other site or means. The right of access to the Platform does not grant to you any right to download or store any Proprietary Materials in any medium.

Beanbag AI reserves all intellectual property rights to the Proprietary Materials, other than as specifically granted under the applicable license granted you under these Terms of Use. No posting, copying, transmission, retransmission, distribution, redistribution, publication, republication, decompilation, disassembling, reverse engineering, or otherwise reproducing, storing, transmitting, modifying, or commercially exploiting any Proprietary Materials in any form or by any means, for any purpose, is permitted without our express written permission.

The entire Platform is protected by copyright. Complying with all applicable copyright laws is your responsibility. If you give feedback on the Platform, for example recommendations for improvements or features, such feedback will be deemed non-confidential and non-proprietary, and implementation of that feedback is owned by us and may become part of the Platform without compensation to you. We reserve all rights in and to the Platform unless we expressly state otherwise.

Other trademarks, service marks, and logos used on the Platform are the trademarks, service marks, or logos of their respective owners. Beanbag AI is using these third-party trademarks, service marks, or logos with the permission of the trademark owner or for identification purposes only.

We may, in appropriate circumstances and at our sole discretion, remove or disable access to any materials on the Platform that we believe (or are notified) may infringe on the rights of others.
`,
    },
    {
      id: "content-usage",
      title: "User Generated Content",
      content: `In these Terms of Use, “User Generated Content” means material (including without limitation text, images, audio material, video material, and audio-visual material) that you submit to the Platform, for whatever purpose. 

You grant to us a worldwide, irrevocable, non-exclusive, royalty-free license to use, reproduce, adapt, publish, translate, communicate, publicly perform, publicly display, and distribute your User Generated Content. You also grant to us the right to sub-license these rights, and the right to bring an action for infringement of these rights. The rights you grant in this license are for the limited purpose of operating, promoting, and improving the Platform.

Your User Generated Content must not be illegal or unlawful, must not infringe any third party’s legal rights, and must not be capable of giving rise to legal action whether against you or us or a third party (in each case under any applicable law). You must not submit any User Generated Content to the Platform that is or has ever been the subject of any threatened or actual legal proceedings or other similar complaint.

We reserve the right to edit or remove any material submitted to the Platform, or stored on our servers, or hosted or published upon the Platform. Notwithstanding our rights under these Terms of Use in relation to User Generated Content, we do not undertake to monitor the submission of such content to, or the publication of such content on, the Platform.
`,
    },
    {
      id: "use-usage",
      title: "Acceptable Use",
      content: `You must not:

Use the Platform in any way that causes, could be reasonably expected to cause, or is intended to cause damage to the Platform or impairment of the availability or accessibility of the Platform; or in any way which is unlawful, illegal, fraudulent, or harmful, or in connection with any unlawful, illegal, fraudulent, or harmful purpose or activity.

Use the Platform to copy, store, host, transmit, send, use, publish, or distribute any material which consists of (or is linked to) any spyware, computer virus, Trojan horse, worm, keystroke logger, rootkit, or other malicious computer software.

Conduct any systematic or automated data collection activities (including without limitation scraping, data mining, data extraction, and data harvesting) on or in relation to our Platform without our express written consent.

Republish material from the Platform (including republication on another website)Sell, rent, or sub-license material from the PlatformReproduce, duplicate, copy, or otherwise exploit material on our Platform for a commercial purpose

Develop, support, or use software, devices, scripts, robots, or any other means or processes (including crawlers, browser plugins and add-ons or any other technology) to scrape the Platform or otherwise copy profiles and other data from the Platform.

Remove or modify any copyright, trademark, legal notices, or other proprietary notations from the Proprietary Materials or any other content available on the Platform;Violate or attempt to violate the Platform’s security mechanisms, attempt to gain unauthorized access to the Platform or assist others to do so, or otherwise breach the security of the Platform or corrupt the Platform in any way.

We reserve the right to monitor use of the Platform and to suspend, revoke, deny, disable, or terminate your access if you violate any provisions of these Terms of Use or if your or behavior exceeds reasonable limits, as determined in our sole discretion.
`,
    },
    {
      id: "warranty-usage",
      title: "Warranty Disclaimer",
      content: `WE DO NOT WARRANT THE COMPLETENESS OR ACCURACY OF THE INFORMATION PUBLISHED ON THE PLATFORM; NOR DO WE COMMIT TO ENSURING THAT THE PLATFORM REMAINS AVAILABLE OR THAT THE MATERIAL ON THE PLATFORM IS KEPT UP-TO-DATE. 

USE OF THE PLATFORM IS AT YOUR OWN RISK. THE PLATFORM IS PROVIDED ON A “AS IS” AND “AS AVAILABLE” BASIS. BEANBAG AI AND ITS AFFILIATES, SUPPLIERS AND PARTNERS EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF TITLE, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT.

BEANBAG AI AND ITS AFFILIATES, SUPPLIERS AND PARTNERS MAKE NO WARRANTY THAT (i) THE PLATFORM OR ANY PRODUCTS PURCHASED THROUGH THE PLATFORM WILL MEET YOUR REQUIREMENTS; (ii) THE PLATFORM WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE; OR (iii) THAT THERE WILL BE NO ERRORS IN THE PLATFORM OR THAT BEANBAG AI WILL FIX ANY ERRORS. ANY MATERIALS OBTAINED THROUGH USE OF THE PLATFORM ARE OBTAINED AT YOUR OWN DISCRETION AND RISK AND Beanbag AI SHALL NOT BE RESPONSIBLE FOR ANY DAMAGE CAUSED TO YOUR COMPUTER OR DATA OR FOR ANY BUGS, VIRUSES, TROJAN HORSES, OR OTHER DESTRUCTIVE CODE RESULTING FROM USE OF THE PLATFORM OR ANY CONTENT OBTAINED FROM THE PLATFORM.

SOME STATES DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES, SO THE ABOVE EXCLUSION MAY NOT APPLY TO YOU. YOU MAY ALSO HAVE OTHER LEGAL RIGHTS, WHICH VARY FROM STATE TO STATE.
`,
    },
    {
      id: "links-usage",
      title: "Links to Other Sites",
      content: `The Platform may contain links to other websites that we do not own or control. We are not responsible for any of these other websites and any links to these other websites should not be interpreted as an endorsement of any company, content, or products. We are not responsible to you for any aspect of these other websites, including their content, privacy policies, or anything else. You may be exposed to things on other websites that you do not like or that you find offensive. We are not responsible for this, either. You must use your own discretion when you go to other websites. You should also read the terms and conditions and privacy policies of these other websites.`,
    },
    {
      id: "export-usage",
      title: "Export Controls",
      content: `The Platform may be subject to export laws and regulations of the United States and other jurisdictions. Each party represents that it is not named on any U.S. government denied-party list. You shall not access or use the Platform in a U.S.-embargoed country or in violation of any U.S. export law or regulation`,
    },
    {
      id: "general-usage",
      title: "General Terms",
      content: `Non-Waiver. Failure by Beanbag AI to enforce any provision(s) of these Terms of Use will not be construed as a waiver of any provision or right.

Severability. If any provision of these Terms of Use is found to be illegal, void, or unenforceable, then that provision will be deemed severable from these Terms of Use and will not affect the validity and enforceability of any remaining provisions of these Terms of Use.

Governing Law and Jurisdiction. These Terms of Use, and all claims or causes of action (whether in contract, tort, or statute) that may be based upon, arise out of, or relate to these Terms of Use, or the performance under these Terms of Use, are governed by and will be construed and enforced in accordance with the laws of the State of Delaware, including its statute of limitations, without reference to (i) any conflicts of law principles that would apply the substantive laws of another jurisdiction to the parties’ rights or duties, (ii) the United Nations Convention on Contracts for the International Sale of Goods, or (iii) other international laws. Any action or other judicial proceeding for the enforcement of these Terms of Use or any of its provisions shall be instituted only in the courts of the State of Delaware.

Compliance with Legal Requests. Without limiting the foregoing, we have the right to fully cooperate with any valid legal process from a law enforcement authority with jurisdiction that requests or directs us to disclose customer data or other information on the Platform. YOU WAIVE AND HOLD HARMLESS US AND OUR AFFILIATES, LICENSEES, AND SERVICE PROVIDERS FROM ANY CLAIMS RESULTING FROM ANY ACTION TAKEN BY US OR ANY OF THE FOREGOING PARTIES DURING OR AS A RESULT OF ITS INVESTIGATIONS AND FROM ANY ACTIONS TAKEN AS A CONSEQUENCE OF INVESTIGATIONS BY EITHER US, SUCH PARTIES, OR LAW ENFORCEMENT AUTHORITIES.
`,
    },
    {
      id: "limitations-usage",
      title: "Limitation of Liability",
      content: `TO THE FULLEST EXTENT PERMITTED UNDER LAW, BEANBAG AI AND ITS AFFILIATES, SUPPLIERS AND PARTNERS HAVE NO OBLIGATION OR LIABILITY (WHETHER ARISING IN CONTRACT, WARRANTY, TORT (INCLUDING NEGLIGENCE), PRODUCT LIABILITY OR OTHERWISE) FOR ANY INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE, OR CONSEQUENTIAL DAMAGES OR LIABILITIES (INCLUDING, BUT NOT LIMITED TO, ANY LOSS OF DATA, REVENUE OR PROFIT) ARISING FROM OR RELATED TO YOUR USE OF THE PLATFORM OR ANY CONTENT PROVIDED BY OR THROUGH THE PLATFORM, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES IN ADVANCE. SOME STATES DO NOT ALLOW THE LIMITATION OR EXCLUSION OF INCIDENTAL, CONSEQUENTIAL OR OTHER TYPES OF DAMAGES, SO SOME OF THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU. NOTWITHSTANDING ANYTHING TO THE CONTRARY CONTAINED HEREIN, BEANBAG AI’S LIABILITY AND THE LIABILITY OF EACH OF ITS OFFICERS, DIRECTORS, INVESTORS, EMPLOYEES, AGENTS, ADVERTISERS, LICENSORS, SUPPLIERS, SERVICE PROVIDERS AND OTHER CONTRACTORS TO YOU OR ANY THIRD PARTIES UNDER ANY CIRCUMSTANCE IS LIMITED TO A MAXIMUM AMOUNT OF $100`,
    },
    {
      id: "Indemnity-usage",
      title: "Indemnity",
      content: `You agree to indemnify and hold harmless Beanbag AI and its affiliates, suppliers, partners, officers, agents, and employees from and against any claim, demand, losses, damages or expenses (including reasonable attorney’s fees) arising from any User Generated Content, your use of the Platform, your connection to the Platform, your violation of these Terms of Use or your violation of any rights of any third-party.`,
    },
    {
      id: "Equitable-usage",
      title: "Equitable Relief",
      content: `You acknowledge that a breach of these Terms of Use may cause us irreparable damage, for which the award of damages would not be adequate compensation. Consequently, you agree that we may institute an action to enjoin you from any and all acts in violation of those provisions, which remedy will be cumulative and not exclusive, and we may seek the entry of an injunction enjoining any breach or threatened breach of those provisions, in addition to any other relief to which we may be entitled at law or in equity.`,
    },
    {
      id: "Assignment-usage",
      title: "Assignment",
      content: `We may freely transfer or assign any portion of our rights or delegate our obligations under these Terms of Use. You may not assign or otherwise transfer your rights, obligations, or duties under these Terms of Use, in whole or in part. `,
    },
    {
      id: "DMCA-usage",
      title: "DMCA",
      content: `It is the policy of Beanbag AI to promptly process and investigate notices of alleged copyright infringement, and take appropriate actions under the Digital Millennium Copyright Act, Title 17, United States Code, Section 512 (“DMCA”). If you are a copyright owner, authorized to act on behalf of one, or authorized to act under any exclusive right under copyright, please report alleged copyright infringements taking place on or through the Platform by providing the following information to Beanbag AI’s Designated Copyright Agent

Identify the copyrighted work that you claim has been infringed, or, if multiple copyrighted works are covered by this DMCA Notice of Alleged Infringement (“Notice”), you may provide a representative list of the copyrighted works that you claim have been infringed;Identify the material or link you claim is infringing (or the subject of infringing activity) and to which access is to be disabled, including at a minimum, if applicable, the URL of the link shown on the Platform or the exact location where such material may be found;Provide your company affiliation (if applicable), mailing address, telephone number, and, if available, email address;

Include the following statements in the body of the Notice:“I hereby state that I have a good faith belief that the disputed use of the copyrighted material is not authorized by the copyright owner, its agent, or the law (e.g., as a fair use).”“I hereby state that the information in this Notice is accurate and, under penalty of perjury, that I am the owner, or authorized to act on behalf of the owner, of the copyright or of an exclusive right under the copyright that is allegedly infringed.”

Provide your full legal name and your electronic or physical signature. The Notice, with all items completed, may be delivered to Beanbag AI’s Designated Copyright Agent by email at raghu@beanbag.ai, or by mail at the following address:

Attn: Copyright Agent
‍
Remote Community Inc.
8, The Green
Suite #15213
Dover, DE 19901

Upon receipt of a Notice, with all items completed, Beanbag AI will take whatever action, in its sole discretion, it deems appropriate, including removal of the challenged content from the Platform. Beanbag AI’s receiving, investigating, or responding to your Notice does not constitute Beanbag AI’s agreement or verification of your claim(s) or any admission of liability therefor.
`,
    },
    {
      id: "Contact-usage",
      title: "Contact",
      content: `Beanbag AI is a Delaware Inc company with its headquarters in Sacramento, CA. If you wish to contact us, please visit here https://www.beanbag.ai/contact-us/`,
    },
  ];

  return (
    <Box
      sx={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{ pt: 4, pb: 8, color: "white", marginTop: "100px" }}
      >
        <Box sx={{ mb: 6, textAlign: "center" }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            Terms of Use
          </Typography>
        </Box>

        <Grid container>
          <Grid item xs={12}>
            {sections.map((section) => (
              <Box
                key={section.id}
                id={section.id}
                sx={{
                  mb: 4,
                  scrollMarginTop: "80px",
                }}
              >
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    fontSize: {
                      xs: "1.75rem",
                      md: "1.2rem",
                    },
                  }}
                >
                  {section.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.7,
                    whiteSpace: "pre-line",
                  }}
                >
                  {section.content}
                </Typography>
              </Box>
            ))}
            <Typography variant="body1" sx={{ mb: 3 }}>
              Last Updated: March 20, 2025
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default TermsOfUse;
